import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, DatePicker, Button, Row, Col, Alert } from 'antd';
import CBInputNumber from '../../../components/CBInputNumber';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { modifierLot } from '../../../services/attestationsService';
import CBModal from '../../../components/CBModal';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../../i18n/format';

export default function EditLotModal({ open, onClose, onUpdated, lot }) {
  const { t } = useTranslation('attestations');
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const dejaDistribue = Number(lot?.quantiteDistribuee) || 0;
  const figePlage = dejaDistribue > 0;

  useEffect(() => {
    if (open && lot) {
      form.setFieldsValue({
        nomFournisseur: lot.nomFournisseur || '',
        numeroBonCommande: lot.numeroBonCommande || '',
        numeroDebutSerie: lot.numeroDebutSerie || '',
        numeroFinSerie: lot.numeroFinSerie || '',
        prixUnitaireAchat: lot.prixUnitaireAchat ?? null,
        dateCommande: lot.dateCommande ? dayjs(lot.dateCommande) : null,
        dateLivraisonFournisseur: lot.dateLivraisonFournisseur ? dayjs(lot.dateLivraisonFournisseur) : null,
      });
    }
  }, [open, lot, form]);

  const debutSaisi = Form.useWatch('numeroDebutSerie', form);
  const finSaisi = Form.useWatch('numeroFinSerie', form);
  const quantiteCalc = useMemo(() => {
    const d = parseInt(String(debutSaisi || '').trim(), 10);
    const f = parseInt(String(finSaisi || '').trim(), 10);
    if (!Number.isFinite(d) || !Number.isFinite(f) || f < d) return null;
    return f - d + 1;
  }, [debutSaisi, finSaisi]);

  const onFinish = async (vals) => {
    if (quantiteCalc == null || quantiteCalc <= 0) {
      toast.error(t('modales.editLot.plageInvalide'));
      return;
    }
    if (figePlage && quantiteCalc < dejaDistribue) {
      toast.error(t('modales.editLot.quantiteInferieure', { n: dejaDistribue }));
      return;
    }
    setSubmitting(true);
    try {
      const updated = await modifierLot(lot.lotTrackingId, {
        referenceLot: lot.referenceLot,
        nomFournisseur: vals.nomFournisseur?.trim() || null,
        numeroBonCommande: vals.numeroBonCommande?.trim() || null,
        quantite: quantiteCalc,
        numeroDebutSerie: String(vals.numeroDebutSerie).trim(),
        numeroFinSerie: String(vals.numeroFinSerie).trim(),
        prixUnitaireAchat: vals.prixUnitaireAchat ?? null,
        dateCommande: vals.dateCommande?.format('YYYY-MM-DD') || null,
        dateLivraisonFournisseur: vals.dateLivraisonFournisseur?.format('YYYY-MM-DD') || null,
      });
      toast.success(t('modales.editLot.modifie', { reference: updated?.referenceLot || lot.referenceLot }));
      onUpdated?.(updated);
      onClose?.();
    } catch (_) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CBModal title={t('modales.editLot.titre', { reference: lot?.referenceLot || '' })}
      open={open}
      onCancel={() => { form.resetFields(); onClose?.(); }}
      footer={null}
      width={680}
      destroyOnClose>
      <Form form={form} layout="vertical" onFinish={onFinish}>

        {figePlage && (
          <Alert type="warning" showIcon style={{ marginBottom: 16 }}
            message={t('modales.editLot.plageFigee')}
            description={t('modales.editLot.plageFigeeDescription', { n: dejaDistribue.toLocaleString(localeIntl()) })} />
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="nomFournisseur" label={t('commun.lot.fournisseur')}>
              <Input placeholder={t('commun.lot.fournisseurPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="numeroBonCommande" label={t('commun.lot.bonCommande')}>
              <Input placeholder={t('commun.optionnel')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={9}>
            <Form.Item name="numeroDebutSerie" label={t('commun.lot.numeroDebut')} rules={[{ required: true }]}>
              <Input disabled={figePlage} placeholder={t('modales.editLot.debutPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item name="numeroFinSerie" label={t('commun.lot.numeroFin')} rules={[{ required: true }]}>
              <Input disabled={figePlage} placeholder={t('modales.editLot.finPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={t('modales.editLot.quantiteAuto')}>
              <Input value={quantiteCalc != null ? quantiteCalc.toLocaleString(localeIntl()) : '-'} disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="prixUnitaireAchat" label={t('modales.editLot.prixAchat')}>
              <CBInputNumber min={0} step={50} style={{ width: '100%' }} placeholder={t('commun.optionnel')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="dateCommande" label={t('commun.colonnes.dateCommande')}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder={t('commun.optionnelle')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="dateLivraisonFournisseur" label={t('modales.editLot.dateLivraisonFournisseur')}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder={t('commun.optionnelle')} />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
          <Button onClick={() => { form.resetFields(); onClose?.(); }}>{t('commun.annuler')}</Button>
          <Button type="primary" htmlType="submit" loading={submitting}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
            {t('modales.editLot.enregistrer')}
          </Button>
        </div>
      </Form>
    </CBModal>
  );
}
